/**
 * CombatScreen — combat rendering surface.
 *
 * # Lifecycle owner of
 *
 *   - `PIXI.Application` via `PixiMount`
 *   - `AtlasLoader` and the textures it loads (Compact biome + Prime sprite)
 *   - The scene-graph containers:
 *       app.stage
 *       ├── worldContainer       (CameraController translates this; sortableChildren + cullable)
 *       │   ├── tileRenderer.build()    (Layer 3 sub-container, added once combat is populated)
 *       │   └── spriteRenderer.build()  (Layer 4a sub-container, added once combat is populated)
 *       └── uiOverlayContainer   (Layer 6c+ adds HUD here; screen-fixed because parent is stage)
 *   - `TileRenderer` (Layer 3 — Compact biome floor + props from a frozen TileMap)
 *   - `SpriteRenderer` (Layer 4a — Prime + future entities; owns its own SpritePool internally)
 *   - `CameraController` (Layer 5 — SmoothDamp follow + bounds + zoom)
 *   - The per-frame ticker callback driving `spriteRenderer.syncFromState`
 *     + `cameraController.update` + viewport-resize detection
 *
 * # Mount sequence
 *
 *   1. Mount the PIXI.Application via `PixiMount.mount()`
 *   2. Construct `worldContainer` + `uiOverlayContainer`; add to stage
 *   3. Construct `AtlasLoader`; kick off parallel atlas loads
 *      (Compact biome + Prime character) via `Promise.all`
 *   4. Construct `CameraController` with renderer-derived viewport dims;
 *      attach to `worldContainer`; install `followPlayer()` resolver
 *   5. Register the per-frame ticker callback (no-ops gracefully until
 *      renderers exist)
 *   6. Subscribe to gameStore via `useGameStore.subscribe(...)`. On every
 *      state change, attempt to build the tile + sprite renderers when
 *      both prerequisites are met:
 *        a. atlases have finished loading
 *        b. `gameStore.state.combat` is non-null
 *      The build is one-shot for the Layer 6a fixture path; Layer 6c+/
 *      Phase 3 will extend to support combat null→non-null cycling per run.
 *
 * # Dispose sequence (reverse of mount; order matters)
 *
 *   1. Unsubscribe the gameStore subscription
 *   2. Remove the ticker callback from `app.ticker`
 *   3. Dispose `CameraController` (releases worldContainer reference; does
 *      NOT destroy the container — CombatScreen owns its lifecycle)
 *   4. Dispose `SpriteRenderer` (returns its sprites to the internal pool
 *      and destroys the pool; detaches from worldContainer)
 *   5. Dispose `TileRenderer` (destroys tile sprites; detaches from
 *      worldContainer)
 *   6. Dispose `AtlasLoader` (releases texture GPU resources — safe AFTER
 *      renderers stop because no live sprite still references the textures)
 *   7. Dispose `PixiMount` (destroys Application, removes canvas from DOM)
 *
 * Order matters because step 6 invalidates textures that steps 2-5 might
 * still be holding. Inverted order risks crashes on dispose under React
 * StrictMode's double-invoke in dev. The dispose order here is the canonical
 * teardown sequence for the entire Phase 2 renderer chain.
 *
 * # Mid-layer architectural pivot (v2.15 demonstration, May 13 2026)
 *
 * The LAYER_6A_IMPLEMENTATION_SPEC.md was authored against an imagined
 * render-layer API surface. Pre-flight recon of the shipped render-layer
 * code (which is FROZEN per v2.15 rule #14) surfaced five call-shape
 * deltas — most notably:
 *   - `TileRenderer` requires a `tileMap` at construction (not a `container`)
 *   - `SpriteRenderer` constructs its own `SpritePool` internally (no
 *     `pool` injection slot); accepts `initialPoolCapacity` instead
 *   - Both renderers' `build()` returns a sub-`Container` the consumer
 *     attaches to its own parent
 *   - `syncFromState` is single-arg; reads `currentTick` from the state
 *
 * Architectural intent preserved; only call shapes adapt. Renderer
 * construction is now deferred until both atlases and combat state are
 * available, which is also the correct production lifecycle for Phase 3+
 * combat null↔non-null cycling.
 *
 * @see ./render/pixiMount
 * @see ./render/atlasLoader
 * @see ./render/tileRenderer
 * @see ./render/spriteRenderer
 * @see ./render/cameraController
 */

import { useEffect, useRef, useState } from 'react';
import { Container, type Ticker } from 'pixi.js';
import {
  TILE_LOGICAL_HEIGHT,
  TILE_LOGICAL_WIDTH,
  type CombatState,
} from '@aevons-wake/core';
import { useGameStore } from './gameStore';
import {
  AtlasLoader,
  CameraController,
  COMPACT_BIOME_MANIFEST,
  DEFAULT_PRIME_ATLAS_RESOLVER,
  followPlayer,
  PRIME_CHARACTER_MANIFEST,
  PixiMount,
  SpriteRenderer,
  TileRenderer,
  type LoadedAtlas,
  type PixelBounds,
} from './render';

interface AtlasLoadProgress {
  readonly compact: number;
  readonly prime: number;
}

const INITIAL_LOAD_PROGRESS: AtlasLoadProgress = { compact: 0, prime: 0 };

/**
 * Default sprite-pool sizing for the dev fixture. Phase 3+ horde mode will
 * tune higher (~500-2000); Phase 2 First Playable is well below this.
 */
const INITIAL_POOL_CAPACITY = 64;

/**
 * Scroll-wheel zoom step. `factor > 1` zooms in on scroll-up, `1/factor`
 * zooms out on scroll-down. 10% per notch is the standard Brotato/Vampire-
 * Survivors feel; tunable per design pass if the scope grows.
 */
const ZOOM_STEP_FACTOR = 1.1;

/** Allowed zoom range. */
const ZOOM_MIN = 0.25;
const ZOOM_MAX = 4;

export interface CombatScreenProps {
  /**
   * Optional callback fired when the user presses Esc or clicks the corner
   * exit button. Wired by App.tsx in dev mode to dispatch the
   * `__DEV_CLEAR_COMBAT` action and clean the `?dev=combat` query string.
   * Omitted (or `undefined`) means no exit affordance is rendered.
   *
   * The explicit `| undefined` is required by `exactOptionalPropertyTypes`
   * — App.tsx passes `import.meta.env.DEV ? handleExitCombat : undefined`,
   * which produces this union type at the call site.
   */
  readonly onExit?: (() => void) | undefined;
}

export function CombatScreen({ onExit }: CombatScreenProps = {}) {
  // ── gameStore subscription for the diagnostic footer ──────────────────
  const combat = useGameStore((s) => s.state.combat);

  // ── DOM container for the PixiJS canvas ───────────────────────────────
  const containerRef = useRef<HTMLDivElement | null>(null);

  // ── Loading / error / ready UI state ──────────────────────────────────
  const [loadProgress, setLoadProgress] =
    useState<AtlasLoadProgress>(INITIAL_LOAD_PROGRESS);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [ready, setReady] = useState(false);

  // ── Lifecycle-owned refs (one set per CombatScreen instance) ──────────
  const pixiMountRef = useRef<PixiMount | null>(null);
  const atlasLoaderRef = useRef<AtlasLoader | null>(null);
  const compactAtlasRef = useRef<LoadedAtlas | null>(null);
  const primeAtlasRef = useRef<LoadedAtlas | null>(null);
  const worldContainerRef = useRef<Container | null>(null);
  const uiOverlayContainerRef = useRef<Container | null>(null);
  const tileRendererRef = useRef<TileRenderer | null>(null);
  const spriteRendererRef = useRef<SpriteRenderer | null>(null);
  const cameraControllerRef = useRef<CameraController | null>(null);
  const tickerCallbackRef = useRef<((ticker: Ticker) => void) | null>(null);
  const gameStoreUnsubRef = useRef<(() => void) | null>(null);
  const lastViewportRef = useRef<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const renderersBuiltRef = useRef(false);

  // Zoom is tracked in BOTH a ref (for the wheel handler's latest-value
  // reads inside its closure) and useState (so the footer re-renders to
  // show the current zoom number). The two are kept in sync at every
  // wheel-handler invocation. React doesn't re-render on ref mutations
  // alone, so a ref-only approach would silently break the displayed value.
  const currentZoomRef = useRef(1);
  const [currentZoom, setCurrentZoom] = useState(1);

  // Hold the latest onExit in a ref so the document-level keydown
  // listener (registered once on mount) can call the current callback
  // even if the prop changes between renders. Prevents listener thrash
  // and the need to add onExit to the effect deps.
  const onExitRef = useRef<typeof onExit>(onExit);
  onExitRef.current = onExit;

  // ── Mount + dispose effect — runs once. StrictMode-safe via the
  //    `cancelled` flag and the idempotent dispose contract that every
  //    Phase 2 renderer guarantees. ───────────────────────────────────────
  useEffect(() => {
    if (!containerRef.current) return;

    let cancelled = false;

    /**
     * Compute pixel bounds from a frozen TileMap's logical dimensions.
     * Uses the LOCKED v2.11 TILE_LOGICAL_WIDTH / TILE_LOGICAL_HEIGHT
     * constants from `@aevons-wake/core`, not the TileMap's `tileSize`
     * field (which is informational).
     */
    function computeBoundsFromTileMap(state: CombatState): PixelBounds {
      return {
        minX: 0,
        minY: 0,
        maxX: state.tileMap.width * TILE_LOGICAL_WIDTH,
        maxY: state.tileMap.height * TILE_LOGICAL_HEIGHT,
      };
    }

    /**
     * Try to construct the tile + sprite renderers. Runs every time the
     * gameStore notifies a state change AND once immediately after atlases
     * resolve. Idempotent: bails out if renderers are already built or
     * prerequisites aren't met.
     */
    function tryBuildRenderers(): void {
      if (renderersBuiltRef.current) return;
      if (cancelled) return;

      const worldContainer = worldContainerRef.current;
      const camera = cameraControllerRef.current;
      const compactAtlas = compactAtlasRef.current;
      const primeAtlas = primeAtlasRef.current;
      if (!worldContainer || !camera || !compactAtlas || !primeAtlas) return;

      const liveCombat = useGameStore.getState().state.combat;
      if (!liveCombat) return;

      // 1. Tile renderer — Compact biome floor + props from frozen TileMap
      const tileRenderer = new TileRenderer({
        tileMap: liveCombat.tileMap,
        atlas: compactAtlas,
      });
      tileRendererRef.current = tileRenderer;
      worldContainer.addChild(tileRenderer.build());

      // 2. Sprite renderer — Prime + future entities. Owns its internal
      //    SpritePool (initialCapacity ≅ peak concurrent entities for
      //    Phase 2 First Playable; Horde Mode tunes higher in Phase 13).
      const spriteRenderer = new SpriteRenderer({
        atlas: primeAtlas,
        initialPoolCapacity: INITIAL_POOL_CAPACITY,
        archetypeResolver: DEFAULT_PRIME_ATLAS_RESOLVER,
        // Layer 6c will inject a Loadout-aware resolver here that closes
        // over `gameState.loadout.cosmeticPaletteId` for the player entity.
      });
      spriteRendererRef.current = spriteRenderer;
      worldContainer.addChild(spriteRenderer.build());

      // 3. Camera — bind bounds to the live TileMap, then snap to target so
      //    the entry transition is clean (no spring lurch from origin).
      camera.setBounds(computeBoundsFromTileMap(liveCombat));
      camera.snapToTarget(liveCombat);

      renderersBuiltRef.current = true;
      setReady(true);
    }

    async function mount(): Promise<void> {
      // 1. Mount PIXI.Application
      const containerEl = containerRef.current;
      if (!containerEl) return;

      const pixi = new PixiMount({ container: containerEl });
      pixiMountRef.current = pixi;
      await pixi.mount();
      if (cancelled) return;

      const app = pixi.getApp();
      const stage = pixi.getStage();

      // 2. Scene-graph containers — worldContainer is sortable + cullable
      //    so the renderer sub-containers cross-sort by their internal
      //    zIndex if needed (Phase 3+ may add more sub-containers).
      const worldContainer = new Container();
      worldContainer.label = 'world-container';
      worldContainer.sortableChildren = true;
      worldContainer.cullable = true;
      stage.addChild(worldContainer);
      worldContainerRef.current = worldContainer;

      const uiOverlayContainer = new Container();
      uiOverlayContainer.label = 'ui-overlay-container';
      stage.addChild(uiOverlayContainer);
      uiOverlayContainerRef.current = uiOverlayContainer;

      // 3. AtlasLoader — parallel load of Compact biome + Prime character
      const loader = new AtlasLoader();
      atlasLoaderRef.current = loader;

      let compactAtlas: LoadedAtlas;
      let primeAtlas: LoadedAtlas;
      try {
        [compactAtlas, primeAtlas] = await Promise.all([
          loader.load(COMPACT_BIOME_MANIFEST, (loaded, total) => {
            setLoadProgress((p) => ({ ...p, compact: loaded / total }));
          }),
          loader.load(PRIME_CHARACTER_MANIFEST, (loaded, total) => {
            setLoadProgress((p) => ({ ...p, prime: loaded / total }));
          }),
        ]);
      } catch (err) {
        if (cancelled) return;
        setLoadError(err as Error);
        return;
      }
      if (cancelled) return;

      compactAtlasRef.current = compactAtlas;
      primeAtlasRef.current = primeAtlas;

      // 4. Camera — viewport from renderer's LOGICAL (CSS-pixel) dimensions,
      //    not the buffer dimensions. PixiJS v8's `renderer.width` /
      //    `renderer.height` return the drawing buffer size (CSS × DPR);
      //    `renderer.screen.width` / `.height` return the logical CSS-px
      //    dimensions. The camera math operates in world coords which equal
      //    logical CSS-px (1 world unit = 1 CSS px) — using buffer-px
      //    instead would over-scale the viewport by `resolution` and push
      //    content off-screen, since the worldContainer's position is
      //    interpreted as world coords (CSS-px) not buffer-px.
      const initialWidth = app.renderer.screen.width;
      const initialHeight = app.renderer.screen.height;
      const camera = new CameraController({
        viewportWidth: initialWidth,
        viewportHeight: initialHeight,
      });
      camera.attach(worldContainer);
      camera.setFollowTarget(followPlayer());
      cameraControllerRef.current = camera;
      lastViewportRef.current = { width: initialWidth, height: initialHeight };

      // 5. Ticker — registered ONCE; no-ops gracefully when renderers
      //    don't yet exist (atlases loaded but combat is still null), and
      //    when combat returns to null between runs (future).
      const tickerCallback = (ticker: Ticker): void => {
        const deltaSeconds = ticker.deltaMS / 1000;
        const liveCombat = useGameStore.getState().state.combat;
        if (!liveCombat) return;

        // Resize detection — read LOGICAL dims (renderer.screen) to match
        // the camera's world-coord space. Two integer compares per frame.
        const currW = app.renderer.screen.width;
        const currH = app.renderer.screen.height;
        if (
          currW !== lastViewportRef.current.width ||
          currH !== lastViewportRef.current.height
        ) {
          camera.setViewportSize(currW, currH);
          lastViewportRef.current = { width: currW, height: currH };
        }

        // Per-frame sync (only when renderers exist)
        if (spriteRendererRef.current) {
          spriteRendererRef.current.syncFromState(liveCombat);
        }
        camera.update(liveCombat, deltaSeconds);
      };
      tickerCallbackRef.current = tickerCallback;
      app.ticker.add(tickerCallback);

      // 6. gameStore subscription — when combat first becomes non-null,
      //    construct the tile + sprite renderers. Also runs once now in
      //    case combat is ALREADY non-null at this point (the dev fixture
      //    can land while atlases are still loading).
      gameStoreUnsubRef.current = useGameStore.subscribe(tryBuildRenderers);
      tryBuildRenderers();
    }

    // Scroll-wheel zoom — wraps CameraController.setZoom() which Layer 5
    // shipped. Listener is attached to the container div (not the document)
    // so scrolling outside the canvas affects page scroll normally.
    // preventDefault on wheel events requires { passive: false }.
    //
    // Updates both the ref (so subsequent wheel events read the latest
    // value inside this closure) AND the React state (so the displayed
    // zoom number in the footer re-renders).
    const handleWheel = (e: WheelEvent): void => {
      const camera = cameraControllerRef.current;
      if (!camera) return;
      e.preventDefault();
      const zoomFactor = e.deltaY < 0 ? ZOOM_STEP_FACTOR : 1 / ZOOM_STEP_FACTOR;
      const nextZoom = Math.max(
        ZOOM_MIN,
        Math.min(ZOOM_MAX, currentZoomRef.current * zoomFactor),
      );
      currentZoomRef.current = nextZoom;
      setCurrentZoom(nextZoom);
      camera.setZoom(nextZoom);
    };
    const containerEl = containerRef.current;
    containerEl.addEventListener('wheel', handleWheel, { passive: false });

    // Esc-to-exit — document-level so it works regardless of focus. The
    // listener reads onExitRef.current at fire time so prop changes
    // between mount/unmount take effect without effect re-registration.
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && onExitRef.current) {
        onExitRef.current();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    void mount();

    return () => {
      containerEl.removeEventListener('wheel', handleWheel);
      document.removeEventListener('keydown', handleKeyDown);
      cancelled = true;

      // 1. Unsubscribe gameStore — no more state-change notifications.
      if (gameStoreUnsubRef.current) {
        gameStoreUnsubRef.current();
        gameStoreUnsubRef.current = null;
      }

      // 2. Remove ticker callback — guard the app.ticker access against
      //    PixiMount having been disposed earlier in a fast double-mount.
      const tickerCallback = tickerCallbackRef.current;
      const pixiInstance = pixiMountRef.current;
      if (tickerCallback && pixiInstance) {
        try {
          pixiInstance.getApp().ticker.remove(tickerCallback);
        } catch {
          // App accessor throws if PixiMount was already disposed via a
          // prior cleanup run; safe to ignore.
        }
      }
      tickerCallbackRef.current = null;

      // 3-5. Renderers + camera — each .dispose() is idempotent and
      //      handles its own scene-graph detachment.
      cameraControllerRef.current?.dispose();
      cameraControllerRef.current = null;

      spriteRendererRef.current?.dispose();
      spriteRendererRef.current = null;

      tileRendererRef.current?.dispose();
      tileRendererRef.current = null;

      // 6. AtlasLoader — safe AFTER renderers stop because no live sprite
      //    still references the textures.
      atlasLoaderRef.current?.dispose();
      atlasLoaderRef.current = null;

      // 7. PixiMount — destroys Application, removes canvas from DOM.
      pixiMountRef.current?.dispose();
      pixiMountRef.current = null;

      // Clear remaining refs so a hot-reload remount starts from a known
      // clean state. Containers and atlases are owned by the disposed
      // primitives above; no further action needed here.
      worldContainerRef.current = null;
      uiOverlayContainerRef.current = null;
      compactAtlasRef.current = null;
      primeAtlasRef.current = null;
      renderersBuiltRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Render ────────────────────────────────────────────────────────────
  if (loadError) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-deep-0">
        <div className="bg-deep-1 border border-red-800 rounded-lg p-6 text-center max-w-md">
          <h2 className="text-red-400 text-lg font-semibold mb-2">
            Combat load failed
          </h2>
          <p className="text-zinc-400 text-sm">{loadError.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full relative bg-deep-0 overflow-hidden">
      {/* PixiJS canvas mount — fills the parent's full dimensions. When
          parent is the fixed-inset-0 wrapper in App.tsx (combat active),
          this is the full viewport. Layer 6a's design intent: combat is
          full-bleed; HUD overlays land on top in Layer 6c+. */}
      <div ref={containerRef} className="absolute inset-0" />

      {/* Loading overlay — centered on top of canvas while atlases load. */}
      {!ready && (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-zinc-400 text-sm font-mono gap-1 pointer-events-none">
          <p>Loading Compact biome: {Math.round(loadProgress.compact * 100)}%</p>
          <p>Loading Prime: {Math.round(loadProgress.prime * 100)}%</p>
        </div>
      )}

      {/* Diagnostic footer — bottom-left overlay, dev/debug only. Lives
          inside CombatScreen rather than its own container so it stays
          legible as a small overlay over the canvas. */}
      {combat && (
        <p className="absolute bottom-2 left-2 text-zinc-500 text-xs font-mono pointer-events-none">
          tick: {combat.currentTick} · biome: {combat.biomeId} · architecture:{' '}
          {combat.architectureId} · zoom: {currentZoom.toFixed(2)}x · scroll to
          zoom · Esc to exit
        </p>
      )}

      {/* Exit affordance — top-right corner X button, only rendered when
          parent supplied an onExit callback. Click OR Esc both fire onExit. */}
      {onExit && (
        <button
          type="button"
          onClick={onExit}
          aria-label="Exit combat (Esc)"
          className="absolute top-3 right-3 w-9 h-9 flex items-center justify-center rounded-md bg-deep-1/80 border border-zinc-700 text-zinc-300 hover:text-zinc-100 hover:bg-deep-1 transition-colors font-mono text-lg"
        >
          ×
        </button>
      )}
    </div>
  );
}
